import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const MODE_KEY = "crismar_mode_v1";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [mode, setModeState] = useState("editor");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const savedMode = localStorage.getItem(MODE_KEY);
        if (savedMode === "editor" || savedMode === "viewer") setModeState(savedMode);
      } catch (e) {
        // ignore
      }
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setHydrated(true);
      }
    })();

    // Global 401 handler → force logout state
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          const url = err?.config?.url || "";
          // Don't trigger logout on /auth/me (that's the initial check)
          // or on /auth/login (that's the login attempt itself)
          if (!url.includes("/auth/me") && !url.includes("/auth/login")) {
            setUser(null);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data.user);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      return {
        ok: false,
        error: typeof detail === "string" ? detail : "Erro ao fazer login",
      };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(null);
  };

  const setMode = (m) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch (e) {
      // ignore
    }
  };

  const canEdit = mode === "editor";
  const isAuthenticated = !!user;
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || isSuperAdmin;

  return (
    <AuthContext.Provider
      value={{
        user,
        userName: user?.name || "Usuário",
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
        hydrated,
        login,
        logout,
        mode,
        setMode,
        canEdit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
